import { localeString } from "@faire/help-center-utils/lib/localeString";
import { getShouldUseZendeskSandbox } from "@faire/help-center-utils/serialized-data/getShouldUseZendeskSandbox";
import searchArticle, {
  QueryParameters as ISearchArticlesRequest,
} from "@faire/web-api--source/endpoints/www/api/zendesk/articles/search/get";
import fetchArticles from "@faire/web-api--source/endpoints/www/api/zendesk/locale/articles/articleId/get";
import articlePage from "@faire/web-api--source/routes/www/support/articles/articleId";

type ArticleData = {
  name: string;
  id: string;
  section?: string;
};

export class Article {
  readonly name: string;
  readonly id: string;
  private readonly section?: string;
  private sandboxArticleId?: string;

  constructor(data: ArticleData) {
    this.name = data.name;
    this.id = data.id;
    this.section = data.section;
  }

  getPreview = async (): Promise<string> => {
    let description = "";
    if (getShouldUseZendeskSandbox()) {
      // override the id with the sandbox one
      this.sandboxArticleId = await getSandboxArticleId(this.name);
      description = await generateDescription(this.sandboxArticleId ?? "");
    } else {
      description = await generateDescription(this.id);
    }
    return description;
  };

  getLink = (): string => {
    return articlePage(this.id, {
      section: this.section,
    });
  };

  /**
   * Creates a new instance of this article overwriting the section
   * @param section
   * @returns
   */
  atSection = (section: string): Article => {
    return new Article({
      name: this.name,
      id: this.id,
      section,
    });
  };
}

/**
 * Returns the article ID from the sandbox Zendesk URL
 * @param productArticleId
 * @returns The sandbox article ID
 */
const getSandboxArticleId = async (
  productionArticleTitle: string
): Promise<string> => {
  // Search article from sandbox Zendesk URL by production title
  const { results } = await searchArticle(
    ISearchArticlesRequest.build({
      query: productionArticleTitle,
      locale: localeString,
    })
  );
  // Return sandbox article ID
  return results?.[0]?.id ? `${results[0].id}` : "";
};

async function generateDescription(articleID: string): Promise<string> {
  try {
    const response = await fetchArticles(localeString, articleID);
    const str = response.article?.body ?? "";
    const text: string = str.replace(/<\/?[^>]+(>|$)/g, "");
    return text;
  } catch (error) {
    return "";
  }
}
